import React, { useEffect, useState } from "react";
import { apiService } from "../../../api/apiService";
import s from "./UserAvatar.module.css";

/**
 * Awatar pracownika — MUST-HAVE aplikacji: wszędzie tam, gdzie wymieniony jest człowiek,
 * ma być jego zdjęcie (własny awatar z profilu, a w braku — zdjęcie z książki adresowej O365,
 * które aplikacja i tak synchronizuje). Odwrót to inicjały na kolorze wyliczonym z id.
 *
 * DLACZEGO WSADOWO: listy (Mój Dział, obsady, raporty) mają dziesiątki wierszy. Żądanie per
 * wiersz to lawina — komponent ZBIERA userId-y pojawiające się w jednym oknie renderu
 * i wysyła JEDNO żądanie /profile/avatars/batch; wynik trafia do pamięci modułu, więc ta sama
 * osoba na kolejnych stronach nie kosztuje już nic.
 */

/** userId → Promise<dataUrl|null> */
const pamiec = new Map();

/** id czekające na wysłanie w najbliższej paczce → ich resolve'y */
let poczekalnia = new Map();
let zaplanowano = false;

/**
 * [R40 2026-09-13] Sufit serwera: `UserProfileController.GetAvatarsBatch` bierze najwyżej 200 id (`Take(200)`).
 * Długa lista (e-teczka, cała firma) wysłana jednym żądaniem dostawała zdjęcia tylko dla pierwszych 200 osób —
 * reszta po cichu zostawała na inicjałach. Paczka jest więc dzielona na kawałki po 200.
 */
const SUFIT_PACZKI = 200;

const wyslijPaczke = async () => {
    const partia = poczekalnia;
    poczekalnia = new Map();
    zaplanowano = false;
    const ids = [...partia.keys()];
    const kawalki = [];
    for (let i = 0; i < ids.length; i += SUFIT_PACZKI) kawalki.push(ids.slice(i, i + SUFIT_PACZKI));
    await Promise.all(kawalki.map(async (kawalek) => {
        try {
            const r = await apiService.getUserAvatarsBatch(kawalek);
            const mapa = r?.data || {};
            kawalek.forEach((id) => partia.get(id)(mapa[String(id)] || null));
        } catch {
            // Brak sieci = inicjały, nie błąd. [R40] Wpis znika z pamięci, żeby następne wejście spróbowało
            // jeszcze raz — inaczej jedna chwilowa awaria zostawiała inicjały aż do przeładowania strony.
            kawalek.forEach((id) => { pamiec.delete(id); partia.get(id)(null); });
        }
    }));
};

const pobierzAwatar = (userId) => {
    if (pamiec.has(userId)) return pamiec.get(userId);
    const obietnica = new Promise((resolve) => {
        poczekalnia.set(userId, resolve);
        if (!zaplanowano) {
            zaplanowano = true;
            // Okno 40 ms: wystarczy, by cała lista zdążyła się zamontować i dopisać do paczki.
            setTimeout(wyslijPaczke, 40);
        }
    });
    pamiec.set(userId, obietnica);
    return obietnica;
};

/**
 * [R40, właściciel 2026-09-13] ZMIANA WŁASNEGO ZDJĘCIA BEZ PRZEŁADOWANIA. Pamięć modułu trzymałaby stare zdjęcie
 * aż do F5 — menu boczne i nagłówek pokazywałyby poprzedni awatar tuż po zapisaniu nowego w „Mój profil”.
 * `odswiezAwatar(id)` usuwa wpis z pamięci i budzi każdy awatar tej osoby na ekranie (jedno nowe żądanie paczki).
 */
const ZDARZENIE_ODSWIEZENIA = "awatar-uzytkownika-zmieniony";
export const odswiezAwatar = (userId) => {
    const id = Number(userId);
    if (!id) return;
    pamiec.delete(id);
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(ZDARZENIE_ODSWIEZENIA, { detail: { userId: id } }));
    }
};

/** Paleta odwrotu — stała per osoba (hash id), czytelna w obu motywach. */
const KOLORY = [
    "#6366F1", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444",
    "#8B5CF6", "#14B8A6", "#F97316", "#EC4899", "#64748B",
];

const inicjalyZ = (firstName, lastName, displayName) => {
    if (firstName || lastName) return `${(firstName || "")[0] || ""}${(lastName || "")[0] || ""}`.toUpperCase();
    const czesci = String(displayName || "").trim().split(/\s+/);
    return czesci.slice(0, 2).map((c) => c[0] || "").join("").toUpperCase() || "?";
};

/**
 * @param {object} props
 * @param {number} props.userId id konta w aplikacji
 * @param {string} [props.firstName]
 * @param {string} [props.lastName]
 * @param {string} [props.displayName] gdy wiersz ma tylko pełne imię i nazwisko
 * @param {number} [props.size] średnica w px (domyślnie 32)
 * @param {string} [props.className] dodatkowa klasa opakowania
 * @param {string} [props.title] dymek; domyślnie pełne imię i nazwisko
 * @param {boolean} [props.wypelnij] [R40] awatar wypełnia pudełko rodzica — rozmiar, zaokrąglenie, tło odwrotu
 *   i poświata zostają z arkusza rodzica (menu boczne, nagłówek); `size` jest wtedy pomijany
 * @param {React.ReactNode} [props.inicjaly] [R40] własny odwrót zamiast wyliczonych inicjałów (np. dotychczasowy
 *   pojedynczy inicjał w menu — wygląd bez zdjęcia ma zostać taki jak był)
 * @param {boolean} [props.dekoracyjny] [R40] imię i nazwisko stoi tuż obok — bez dymka i etykiety, żeby czytnik
 *   ekranu nie czytał nazwiska dwa razy
 */
const UserAvatar = ({
    userId, firstName, lastName, displayName, size = 32, className, title,
    wypelnij = false, inicjaly, dekoracyjny = false,
}) => {
    /* [R40 poprawka 2026-09-13] Zdjęcie jest trzymane RAZEM z id osoby, dla której przyszło. To samo pudełko potrafi
       zmienić osobę bez ponownego montażu (nagłówek e-teczki `wybrany.id`, wybrany pracownik w DepartmentMemberPicker):
       zdjęcie poprzedniej osoby nie może stać przy nowej, zanim nowa się wczyta. */
    const [zdjecie, setZdjecie] = useState({ id: null, url: null });
    const [wersja, setWersja] = useState(0);
    const idOsoby = Number(userId) || null;

    useEffect(() => {
        if (!userId) return undefined;
        const naZmiane = (e) => { if (Number(e?.detail?.userId) === Number(userId)) setWersja((w) => w + 1); };
        window.addEventListener(ZDARZENIE_ODSWIEZENIA, naZmiane);
        return () => window.removeEventListener(ZDARZENIE_ODSWIEZENIA, naZmiane);
    }, [userId]);

    useEffect(() => {
        if (!idOsoby) return undefined;
        /* [R40 poprawka] BYŁO ŹLE: jeden wspólny ref „żywy” — sprzątanie ustawiało go na false, a NASTĘPNY przebieg
           z powrotem na true, więc spóźniona odpowiedź dla POPRZEDNIEGO id przechodziła i wstawiała cudzą twarz pod
           nową osobę. Flaga jest teraz lokalna dla każdego przebiegu efektu. */
        let aktualny = true;
        pobierzAwatar(idOsoby).then((u) => { if (aktualny) setZdjecie({ id: idOsoby, url: u }); });
        return () => { aktualny = false; };
    }, [idOsoby, wersja]);

    const src = idOsoby && zdjecie.id === idOsoby ? zdjecie.url : null;

    const kolor = KOLORY[Math.abs(Number(userId) || 0) % KOLORY.length];
    const etykieta = dekoracyjny
        ? undefined
        : (title || displayName || `${firstName || ""} ${lastName || ""}`.trim() || undefined);
    const styl = wypelnij
        ? (src ? { background: "var(--color-bg-secondary)" } : undefined)
        : {
            width: size,
            height: size,
            fontSize: Math.max(10, Math.round(size * 0.38)),
            background: src ? "var(--color-bg-secondary)" : `${kolor}22`,
            color: kolor,
        };

    return (
        <span
            className={`${s.avatar}${wypelnij ? ` ${s.wypelnij}` : ""} ${className || ""}`}
            style={styl}
            title={etykieta}
            aria-label={etykieta}
            aria-hidden={dekoracyjny ? "true" : undefined}
        >
            {src ? (
                /* Uszkodzony obraz wraca do inicjałów — pusty kwadrat jest gorszy niż brak zdjęcia. */
                <img src={src} alt="" className={s.photo} onError={() => setZdjecie((z) => (z.id === idOsoby ? { ...z, url: null } : z))} />
            ) : (
                inicjaly !== undefined ? inicjaly : inicjalyZ(firstName, lastName, displayName)
            )}
        </span>
    );
};

export default UserAvatar;
